import { useState, useRef, useEffect } from 'react';
import { Text, Button, Badge, Input, Spinner, Card } from '@fluentui/react-components';
import { ArrowLeftRegular, SendRegular, ArrowDownloadRegular, ChatRegular, DeleteRegular } from '@fluentui/react-icons';
import ReactMarkdown from 'react-markdown';
import remarkGfm from 'remark-gfm';
import { useIntl } from 'react-intl';
import type { Project } from '../services/project-service';
import { useApiToken } from '../hooks/useApiToken';
import { apiUrl } from '../utils/api-url';

interface ChatMessage {
  role: 'user' | 'assistant' | 'system';
  content: string;
  timestamp?: string;
}

interface AssessmentChatPageProps {
  project: Project;
  onBack: () => void;
}

const DOMAIN_LABEL_KEYS: Record<string, string> = {
  'business-alignment': 'assessmentChat.domain.businessAlignment',
  'current-state': 'assessmentChat.domain.currentState',
  'cloud-readiness': 'assessmentChat.domain.cloudReadiness',
  'technical-assessment': 'assessmentChat.domain.technicalAssessment',
  'security-compliance': 'assessmentChat.domain.securityCompliance',
  'operations': 'assessmentChat.domain.operations',
  'financial': 'assessmentChat.domain.financial',
};

export function AssessmentChatPage({ project, onBack }: AssessmentChatPageProps) {
  const intl = useIntl();
  const getToken = useApiToken();
  const [messages, setMessages] = useState<ChatMessage[]>([]);
  const [input, setInput] = useState('');
  const [sending, setSending] = useState(false);
  const [currentDomain, setCurrentDomain] = useState('');
  const [completedDomains, setCompletedDomains] = useState<string[]>([]);
  const [findingsCount, setFindingsCount] = useState(0);
  const scrollRef = useRef<HTMLDivElement>(null);

  // Auto-scroll to bottom
  useEffect(() => {
    scrollRef.current?.scrollTo({ top: scrollRef.current.scrollHeight, behavior: 'smooth' });
  }, [messages]);

  // Show welcome on mount — user initiates the conversation
  useEffect(() => {
    setMessages([{
      role: 'system',
      content: intl.formatMessage({ id: 'assessmentChat.welcome' }, { name: project.name }),
    }]);
  }, [project.name]); // eslint-disable-line react-hooks/exhaustive-deps

  const handleReset = async () => {
    if (!confirm(intl.formatMessage({ id: 'assessmentChat.reset.confirm' }))) return;
    try {
      const token = await getToken();
      // Delete the assessment document
      await fetch(apiUrl(`/api/documents/case-${project.id}?customerId=${encodeURIComponent(project.customerId)}`), {
        method: 'DELETE',
        headers: { Authorization: `Bearer ${token}` },
      });
      // Clear local state
      setMessages([{
        role: 'system',
        content: intl.formatMessage({ id: 'assessmentChat.reset.notice' }),
      }]);
      setCurrentDomain('');
      setCompletedDomains([]);
      setFindingsCount(0);
    } catch (err) {
      console.error('Reset failed:', err);
    }
  };

  const sendMessage = async (text: string) => {
    if (!text.trim()) return;

    const userMsg: ChatMessage = { role: 'user', content: text, timestamp: new Date().toISOString() };
    setMessages(prev => [...prev, userMsg]);
    setInput('');
    setSending(true);

    // Add placeholder assistant message that we'll stream into
    const assistantIdx = messages.length + 1; // +1 for the user message we just added
    setMessages(prev => [...prev, { role: 'assistant', content: '', timestamp: new Date().toISOString() }]);

    try {
      const token = await getToken();

      // Try SSE streaming endpoint first
      const res = await fetch(apiUrl(`/api/stream/assessment/${project.id}/chat?customerId=${encodeURIComponent(project.customerId)}`), {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` },
        body: JSON.stringify({ message: text }),
      });

      if (res.ok && res.headers.get('content-type')?.includes('text/event-stream')) {
        // Stream SSE tokens
        const reader = res.body?.getReader();
        const decoder = new TextDecoder();
        let accumulated = '';

        if (reader) {
          while (true) {
            const { done, value } = await reader.read();
            if (done) break;

            const chunk = decoder.decode(value, { stream: true });
            const lines = chunk.split('\n');

            for (const line of lines) {
              if (line.startsWith('data: ')) {
                const data = line.slice(6);
                try {
                  const parsed = JSON.parse(data);
                  if (parsed.text) {
                    accumulated += parsed.text;
                    setMessages(prev => {
                      const updated = [...prev];
                      if (updated[assistantIdx]) {
                        updated[assistantIdx] = { ...updated[assistantIdx], content: accumulated };
                      }
                      return updated;
                    });
                  }
                  if (parsed.currentDomain) setCurrentDomain(parsed.currentDomain);
                  if (parsed.completedDomains) setCompletedDomains(parsed.completedDomains);
                  if (parsed.findingsCount !== undefined) setFindingsCount(parsed.findingsCount);
                  if (parsed.error) throw new Error(parsed.error);
                } catch { /* skip unparseable lines */ }
              }
            }
          }
        }
      } else {
        // Fallback to non-streaming endpoint
        const fallbackRes = await fetch(apiUrl(`/api/assessment/${project.id}/chat?customerId=${encodeURIComponent(project.customerId)}`), {
          method: 'POST',
          headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` },
          body: JSON.stringify({ message: text }),
        });
        if (!fallbackRes.ok) throw new Error(`API error: ${fallbackRes.status}`);
        const data = await fallbackRes.json();
        setMessages(prev => {
          const updated = [...prev];
          if (updated[assistantIdx]) {
            updated[assistantIdx] = { ...updated[assistantIdx], content: data.reply };
          }
          return updated;
        });
        if (data.currentDomain) setCurrentDomain(data.currentDomain);
        if (data.completedDomains) setCompletedDomains(data.completedDomains);
        if (data.findingsCount !== undefined) setFindingsCount(data.findingsCount);
      }
    } catch (err) {
      setMessages(prev => [...prev, { role: 'system', content: `Error: ${err instanceof Error ? err.message : 'Unknown error'}` }]);
    } finally {
      setSending(false);
    }
  };

  const handleKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === 'Enter' && !e.shiftKey && !sending) {
      e.preventDefault();
      sendMessage(input);
    }
  };

  const handleExportCsv = async () => {
    try {
      const token = await getToken();
      const res = await fetch(apiUrl(`/api/assessment/${project.id}/case-csv?customerId=${encodeURIComponent(project.customerId)}`), {
        headers: { Authorization: `Bearer ${token}` },
      });
      if (!res.ok) throw new Error('Export failed');
      const blob = await res.blob();
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `CASE_Assessment_${project.name.replace(/\s/g, '_')}.csv`;
      a.click();
      URL.revokeObjectURL(url);
    } catch (err) {
      alert(`Export failed: ${err instanceof Error ? err.message : 'Unknown error'}`);
    }
  };

  return (
    <div className="page-root assessment-chat-page">
      <div className="page-title-bar">
        <Button appearance="subtle" icon={<ArrowLeftRegular />} onClick={onBack} title={intl.formatMessage({ id: 'assessmentChat.backButton.title' })} />
        <ChatRegular className="icon-title text-brand" />
        <Text size={500} weight="semibold">{intl.formatMessage({ id: 'assessmentChat.title' })}</Text>
        <div className="title-bar-actions">
          {currentDomain && <Badge size="small" color="brand">{DOMAIN_LABEL_KEYS[currentDomain] ? intl.formatMessage({ id: DOMAIN_LABEL_KEYS[currentDomain] }) : currentDomain}</Badge>}
          <Badge size="small" appearance="outline">{intl.formatMessage({ id: 'assessmentChat.badge.domains' }, { count: completedDomains.length })}</Badge>
          <Badge size="small" appearance="outline">{intl.formatMessage({ id: 'assessmentChat.badge.findings' }, { count: findingsCount })}</Badge>
          <Button appearance="subtle" icon={<ArrowDownloadRegular />} onClick={handleExportCsv} title={intl.formatMessage({ id: 'assessmentChat.exportButton.title' })} />
          <Button appearance="subtle" icon={<DeleteRegular />} onClick={handleReset} title={intl.formatMessage({ id: 'assessmentChat.resetButton.title' })} />
        </div>
      </div>

      <div className="assessment-domains-bar">
        {Object.entries(DOMAIN_LABEL_KEYS).map(([key, labelKey]) => (
          <Badge
            key={key}
            size="small"
            color={completedDomains.includes(key) ? 'success' : key === currentDomain ? 'brand' : 'informative'}
            appearance={completedDomains.includes(key) || key === currentDomain ? 'filled' : 'outline'}
          >
            {intl.formatMessage({ id: labelKey })}
          </Badge>
        ))}
      </div>

      <div className="chat-messages" ref={scrollRef}>
        {messages.map((msg, i) => (
          <div key={i} className={`chat-message chat-message--${msg.role}`}>
            {msg.role === 'system' ? (
              <Card className="chat-system-card">
                <Text size={200} className="text-muted">{msg.content}</Text>
              </Card>
            ) : (
              <div className="chat-bubble">
                <div className="chat-bubble-header">
                  <Text size={200} weight="semibold">{msg.role === 'user' ? intl.formatMessage({ id: 'assessmentChat.bubble.user' }) : intl.formatMessage({ id: 'assessmentChat.bubble.agent' })}</Text>
                </div>
                <div className="chat-bubble-content">
                  <ReactMarkdown remarkPlugins={[remarkGfm]}>{msg.content}</ReactMarkdown>
                </div>
              </div>
            )}
          </div>
        ))}
        {sending && (
          <div className="chat-message chat-message--assistant">
            <div className="chat-bubble">
              <Spinner size="tiny" label={intl.formatMessage({ id: 'assessmentChat.thinking' })} />
            </div>
          </div>
        )}
      </div>

      <div className="chat-input-bar">
        <Input
          className="chat-input"
          placeholder={intl.formatMessage({ id: 'assessmentChat.input.placeholder' })}
          value={input}
          onChange={(_e, d) => setInput(d.value)}
          onKeyDown={handleKeyDown}
          disabled={sending}
        />
        <Button
          appearance="primary"
          icon={<SendRegular />}
          onClick={() => sendMessage(input)}
          disabled={sending || !input.trim()}
        >
          {intl.formatMessage({ id: 'assessmentChat.sendButton' })}
        </Button>
      </div>
    </div>
  );
}
